import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, KeyRound, Copy, Check, RefreshCw } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Switch } from "components/ui/switch";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

function SecretModal({ clientId, secret, onClose }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState("");
  const copy = (text, id) => {
    navigator.clipboard?.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };
  const Row = ({ label, value, id }) => (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
        <code className="flex-1 text-xs break-all">{value}</code>
        <button onClick={() => copy(value, id)} className="text-muted-foreground hover:text-foreground">
          {copied === id ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-lg border border-border p-6 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t("clientAppForm.credentialsTitle")}</h2>
        </div>
        <p className="text-sm text-amber-600 dark:text-amber-400">
          {t("clientAppForm.secretWarnPrefix")} <strong>client secret</strong> {t("clientAppForm.secretWarnSuffix")}
        </p>
        <Row label={t("clientApps.clientId")} value={clientId} id="cid" />
        <Row label={t("clientAppForm.clientSecret")} value={secret} id="csk" />
        <div className="flex justify-end">
          <Button onClick={onClose}>{t("clientAppForm.done")}</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// "" on the three policy fields means "herdar o padrão global" (null no backend).
const EMPTY = {
  name: "", webhook_url: "", webhook_secret: "", redirect_uri_allowlist: "",
  require_geo: "", require_gesture: "", ai_threshold: "", is_active: true, company: "", min_tier: "bronze",
  email_reports: false, report_emails: "",
};

const triState = (v) => (v === null || v === undefined ? "" : v ? "true" : "false");

export default function ClientAppForm() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);

  const [form, setForm] = useState(EMPTY);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [secret, setSecret] = useState(null); // {clientId, secret}
  const [clientId, setClientId] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [exampleCfg, setExampleCfg] = useState(null);
  const [copiedCfg, setCopiedCfg] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const genExampleConfig = async () => {
    try {
      const r = await api.get(`/api/client-apps/${id}/example-config/`);
      setExampleCfg(r.data.config || "");
    } catch (e) {
      setError(e?.response?.data?.detail || t("clientAppForm.cfgError", "Falha ao gerar a configuração."));
    }
  };
  const copyCfg = () => { navigator.clipboard?.writeText(exampleCfg || ""); setCopiedCfg(true); setTimeout(() => setCopiedCfg(false), 1500); };

  const copyWebhookSecret = () => {
    if (!form.webhook_secret) return;
    navigator.clipboard?.writeText(form.webhook_secret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 1500);
  };

  useEffect(() => {
    if (user?.is_admin) {
      api.get("/api/companies/").then((r) => setCompanies(r.data)).catch(() => {});
    }
  }, [user]);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/client-apps/${id}/`);
      const d = res.data;
      setClientId(d.client_id);
      setForm({
        name: d.name || "",
        webhook_url: d.webhook_url || "",
        webhook_secret: d.webhook_secret || "",
        redirect_uri_allowlist: (d.redirect_uri_allowlist || []).join("\n"),
        require_geo: triState(d.require_geo),
        require_gesture: triState(d.require_gesture),
        ai_threshold: d.ai_threshold ?? "",
        min_tier: d.min_tier || "bronze",
        is_active: !!d.is_active,
        company: d.company || "",
        email_reports: !!d.email_reports,
        report_emails: (d.report_emails || []).join("\n"),
      });
    } catch {
      setError(t("clientAppForm.loadError"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (editing) load(); }, [editing, load]);

  // "" -> null (herdar global) for the three policy fields.
  const orNull = (v) => (v === "" ? null : v);
  const payload = () => ({
    name: form.name,
    webhook_url: form.webhook_url,
    // Vazio = manter o secret atual (o backend nunca apaga a chave do HMAC).
    ...(form.webhook_secret.trim() ? { webhook_secret: form.webhook_secret.trim() } : {}),
    redirect_uri_allowlist: form.redirect_uri_allowlist
      .split("\n").map((s) => s.trim()).filter(Boolean),
    require_geo: form.require_geo === "" ? null : form.require_geo === "true",
    require_gesture: form.require_gesture === "" ? null : form.require_gesture === "true",
    ai_threshold: orNull(form.ai_threshold) === null ? null : Number(form.ai_threshold),
    min_tier: form.min_tier || "bronze",
    is_active: form.is_active,
    email_reports: form.email_reports,
    report_emails: form.report_emails.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean),
    ...(user?.is_admin && !editing ? { company: form.company } : {}),
  });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/client-apps/${id}/`, payload());
        navigate("/liveness/admin/client-apps");
      } else {
        const res = await api.post("/api/client-apps/", payload());
        setSecret({ clientId: res.data.client_id, secret: res.data.client_secret });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || t("clientAppForm.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const rotate = async () => {
    setError("");
    try {
      const res = await api.post(`/api/client-apps/${id}/rotate-secret/`);
      setSecret({ clientId: res.data.client_id, secret: res.data.client_secret });
    } catch {
      setError(t("clientAppForm.rotateError"));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;
  }

  const field = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";

  return (
    <div className="animate-fade-in w-full">
      <button onClick={() => navigate("/liveness/admin/client-apps")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>
      <h1 className="text-2xl font-bold tracking-tight mb-6">
        {editing ? t("clientAppForm.editTitle") : t("clientAppForm.newTitle")}
      </h1>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={submit} className="w-full space-y-5">
            {user?.is_admin && !editing && (
              <div>
                <label className="block text-sm font-medium mb-1">{t("common.company")}</label>
                <select className={field} value={form.company}
                  onChange={(e) => set("company", e.target.value)} required>
                  <option value="">{t("common.selectCompany")}</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">{t("common.name")}</label>
              <input className={field} value={form.name}
                onChange={(e) => set("name", e.target.value)} required />
            </div>

            {editing && (
              <div>
                <label className="block text-sm font-medium mb-1">{t("clientApps.clientId")}</label>
                <div className="flex items-center gap-2">
                  <input className={field} value={clientId} readOnly />
                  <Button type="button" variant="outline" onClick={rotate} title={t("clientAppForm.rotateSecret")}>
                    <RefreshCw className="h-4 w-4 mr-2" /> {t("clientAppForm.rotateSecret")}
                  </Button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">{t("clientAppForm.webhookUrl")}</label>
              <input className={field} type="url" placeholder="https://…" value={form.webhook_url}
                onChange={(e) => set("webhook_url", e.target.value)} />
              <p className="text-xs text-muted-foreground mt-1">
                {t("clientAppForm.webhookUrlHelpPrefix")} <code>deep.completed</code> {t("clientAppForm.webhookUrlHelpSuffix")}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("clientAppForm.webhookSecret")}</label>
              <div className="flex items-center gap-2">
                <input className={`${field} font-mono text-xs`} value={form.webhook_secret}
                  onChange={(e) => set("webhook_secret", e.target.value)}
                  placeholder={t("clientAppForm.webhookSecretPlaceholder")} />
                <button type="button" onClick={copyWebhookSecret}
                  className="p-2 text-muted-foreground hover:text-foreground" title={t("common.copy")}>
                  {copiedSecret ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t("clientAppForm.webhookSecretHelpPrefix")} <code>{t("clientAppForm.webhookSecretHelpCode")}</code>.
                {" "}{t("clientAppForm.webhookSecretHelpSuffix")}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("clientAppForm.redirectUris")}</label>
              <textarea className={field} rows={3} placeholder={t("clientAppForm.redirectUrisPlaceholder")}
                value={form.redirect_uri_allowlist}
                onChange={(e) => set("redirect_uri_allowlist", e.target.value)} />
              <p className="text-xs text-muted-foreground mt-1">{t("clientAppForm.redirectUrisHelp")}</p>
            </div>

            <p className="text-xs text-muted-foreground -mb-2">
              {t("clientAppForm.policyNotePrefix")} <em>{t("clientAppForm.policyNoteInherit")}</em> {t("clientAppForm.policyNoteMiddle")} <code>/api/v1/liveness/start</code>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t("clientAppForm.aiThreshold")}</label>
                <input className={field} type="number" min={0} max={100} placeholder={t("clientAppForm.inheritGlobal")}
                  value={form.ai_threshold} onChange={(e) => set("ai_threshold", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t("clientAppForm.requireGeo")}</label>
                <select className={field} value={form.require_geo}
                  onChange={(e) => set("require_geo", e.target.value)}>
                  <option value="">{t("clientAppForm.inheritGlobal")}</option>
                  <option value="true">{t("clientAppForm.yes")}</option>
                  <option value="false">{t("clientAppForm.no")}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t("clientAppForm.requireGesture")}</label>
                <select className={field} value={form.require_gesture}
                  onChange={(e) => set("require_gesture", e.target.value)}>
                  <option value="">{t("clientAppForm.inheritGlobal")}</option>
                  <option value="true">{t("clientAppForm.yes")}</option>
                  <option value="false">{t("clientAppForm.no")}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t("clientAppForm.minTier", "Nível mínimo (biometria)")}</label>
                <select className={field} value={form.min_tier}
                  onChange={(e) => set("min_tier", e.target.value)}>
                  <option value="bronze">{t("bio.bronze", "Bronze")}</option>
                  <option value="silver">{t("bio.silver", "Prata")}</option>
                  <option value="gold">{t("bio.gold", "Ouro")}</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("clientAppForm.minTierHelp", "Nível de confiança mínimo exigido para autenticar por biometria neste app.")}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <Switch checked={form.email_reports} onChange={(v) => set("email_reports", v)}
                  ariaLabel={t("clientAppForm.emailReports", "Enviar reports por e-mail ao cliente")} />
                <div>
                  <span className="font-medium">{t("clientAppForm.emailReports", "Enviar reports por e-mail ao cliente")}</span>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("clientAppForm.emailReportsHelp", "Quando ativo, o PDF assinado é enviado por e-mail a cada sessão de liveness aprovada.")}
                  </p>
                </div>
              </div>
              {form.email_reports && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t("clientAppForm.reportEmails", "E-mails destinatários")}
                  </label>
                  <textarea className={field} rows={2}
                    placeholder={t("clientAppForm.reportEmailsPlaceholder", "um por linha ou separados por vírgula")}
                    value={form.report_emails}
                    onChange={(e) => set("report_emails", e.target.value)} />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("clientAppForm.reportEmailsHelp", "Destinatários que receberão o report de cada verificação aprovada.")}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Switch checked={form.is_active} onChange={(v) => set("is_active", v)} ariaLabel={t("clientApps.active")} />
              <span>{t("clientApps.active")}</span>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex justify-between gap-3 pt-2">
              {editing ? (
                <Button type="button" variant="outline" onClick={genExampleConfig}>
                  {t("clientAppForm.genConfig", "Gerar config de exemplo (.env)")}
                </Button>
              ) : <span />}
              <div className="flex gap-3">
                <Button type="button" variant="outline"
                  onClick={() => navigate("/liveness/admin/client-apps")}>{t("common.cancel")}</Button>
                <Button type="submit" loading={saving}>{editing ? t("common.save") : t("common.create")}</Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {secret && (
        <SecretModal clientId={secret.clientId} secret={secret.secret}
          onClose={() => { setSecret(null); navigate("/liveness/admin/client-apps"); }} />
      )}

      {exampleCfg != null && createPortal(
        <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-card border border-border shadow-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">{t("clientAppForm.genConfigTitle", "Configuração de exemplo (RP)")}</h2>
              <button onClick={() => setExampleCfg(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {t("clientAppForm.genConfigHint", "Preencha o SEC_FACE_OIDC_CLIENT_SECRET com o segredo do app (mostrado ao criar/rotacionar).")}
            </p>
            <textarea readOnly value={exampleCfg} rows={18}
              className="w-full rounded-lg border border-border bg-muted text-xs font-mono p-3" />
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={copyCfg}>{copiedCfg ? t("clientAppForm.copied", "Copiado!") : t("clientAppForm.copy", "Copiar")}</Button>
              <Button size="sm" onClick={() => setExampleCfg(null)}>{t("common.close", "Fechar")}</Button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
